import {
  Badge,
  Checkbox,
  HStack,
  Skeleton,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  Avatar,
  type BodyColumnDef,
  DataTable,
  Link,
  ModalConfirm,
  UserTooltip,
} from "@components/ui-kit";
import { StarButton } from "@components/ui-kit/star-button";
import { Trans, t } from "@lingui/macro";
import {
  ProjectOrderByColumnEnum,
  ProjectTypeEnum,
} from "@src/__generated__/urql-graphql";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { ExternalAccessIcon } from "@src/components/widgets/ExternalAccessIcon";
import { ProjectCell } from "@src/components/widgets/TableCell";
import { kebabCase } from "lodash";
import { observer } from "mobx-react-lite";
import { type FC, useMemo } from "react";
import { BUDGET_ITEM_HASH_INDEX_SEPARATOR } from "../../detail/BudgetItems/ItemTitle";
import type { Project } from "../models";
import { useStore } from "../Store";
import Column from "./column";

type ProjectSubRow = Project["__subRows"][0];

type ProjectTableRow =
  | {
      id: string;
      rowType: "project";
      project: Project;
      __subRows?: ProjectTableRow[];
    }
  | {
      id: string;
      rowType: "subRow";
      project: Project;
      subRow: ProjectSubRow;
    };

const isProjectRow = (
  row: ProjectTableRow,
): row is Extract<ProjectTableRow, { rowType: "project" }> =>
  row.rowType === "project";

const TableBody: FC<{ fetching?: boolean }> = ({ fetching }) => {
  const store = useStore();

  const data: ProjectTableRow[] = store.projects.map((project) => {
    const subRows: ProjectTableRow[] = project.__subRows.map((subRow) => ({
      id: subRow.id,
      rowType: "subRow",
      project,
      subRow,
    }));

    return {
      id: project.id,
      rowType: "project",
      project,
      __subRows: subRows.length > 0 ? subRows : undefined,
    };
  });

  const columns = useMemo<
    BodyColumnDef<ProjectTableRow, ProjectOrderByColumnEnum>[]
  >(
    () => [
      {
        id: "projectManager",
        size: 50,
        meta: { label: t`PM` },
        header: t`PM`,
        orderBy: ProjectOrderByColumnEnum.ProjectManager,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const { projectManager } = row.original.project;

          return (
            <UserTooltip user={projectManager}>
              <Avatar
                name={projectManager?.full_name}
                src={projectManager?.image?.urls.thumbnail}
                colorScheme={projectManager?.profile?.hex_color}
              />
            </UserTooltip>
          );
        },
      },
      {
        id: "title",
        meta: { label: t`Project` },
        header: t`Project`,
        orderBy: ProjectOrderByColumnEnum.ProjectCode,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            const project = row.original.project;
            return (
              <ProjectCell.Title project={project} brand={project.brand} />
            );
          }

          const { project, subRow } = row.original;
          const label = `${subRow.title} - ${subRow.categoryName}`;

          return (
            <Tooltip hasArrow label={label} placement="top" shouldWrapChildren>
              <Link
                href={{
                  pathname: "/projects/detail",
                  query: { id: project.id },
                  hash: kebabCase(
                    `${subRow.title}${BUDGET_ITEM_HASH_INDEX_SEPARATOR}0`,
                  ),
                }}
              >
                {label}
              </Link>
            </Tooltip>
          );
        },
      },
      {
        id: "budget",
        meta: { label: t`Budget`, align: "right" },
        header: t`Budget`,
        orderBy: ProjectOrderByColumnEnum.TotalBudget,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            const project = row.original.project;

            return (
              <Column.Budget
                billable={project.billable}
                fetching={fetching}
                budget={project.stats?.total_budget ?? 0}
                isNewerFinalVersionAvailable={
                  project.budgetovacBudget?.newer_final_version_available
                }
                isInternallyApproved={
                  fetching ? null : project.hasBudgetsInternallyApproved
                }
                billingPlanIsMissing={project.billingPlanIsMissing}
                isTimeAndMaterials={
                  project.type === ProjectTypeEnum.TimeMaterials
                }
              />
            );
          }

          return (
            <Column.Budget
              budget={row.original.subRow.stats.total_budget}
              billable
            />
          );
        },
      },
      {
        id: "ourWork",
        size: 180,
        meta: { label: t`Work` },
        header: t`Work`,
        orderBy: ProjectOrderByColumnEnum.SpentOurWork,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            const project = row.original.project;

            return (
              <Column.OurWork
                totalBudget={project.stats?.ourWork.total_budget ?? 0}
                spentBudget={project.stats?.ourWork.spent_budget ?? 0}
                spentBudgetPercent={
                  +(project.stats?.ourWork?.spent_budget_percent ?? 0)
                }
                spentTime={project.stats?.ourWork.spent_time ?? 0}
                spentTimeEstimate={
                  project.lastWorkInProgressProjectStat?.spent_time_estimate ??
                  0
                }
                progress={project.lastWorkInProgressProjectStat?.progress ?? 0}
                progressPercent={
                  +(
                    project.lastWorkInProgressProjectStat?.progress_percent ?? 0
                  )
                }
              />
            );
          }

          const { project, subRow } = row.original;

          return (
            <Column.OurWork
              editable
              item={subRow}
              project={project}
              totalBudget={subRow.stats.total_budget}
              spentBudgetPercent={+subRow.stats.spent_budget_percent}
              spentBudget={subRow.stats.spent_budget}
              spentTime={subRow.stats.spent_time}
              progressPercent={
                +(subRow.lastWorkInProgressItemStat?.progress_percent ?? 0)
              }
              progress={subRow.lastWorkInProgressItemStat?.progress ?? 0}
              spentTimeEstimate={
                subRow.lastWorkInProgressItemStat?.spent_time_estimate ?? 0
              }
            />
          );
        },
      },
      {
        id: "expenses",
        meta: { label: t`Expenses` },
        header: t`Expenses`,
        orderBy: ProjectOrderByColumnEnum.RealExpenses,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const project = row.original.project;

          return (
            <Column.Expenses
              fetching={fetching}
              allItemsCompleted={
                project?.stats?.expenses.all_items_completed ?? false
              }
              showCommission={project.billable}
              showFinalCommission={
                !project.billable && project.stats?.expenses.all_items_completed
              }
              plannedExpenses={project.stats?.expenses.planned_expenses ?? 0}
              plannedCommission={project.stats?.expenses.planned_commission}
              plannedCommissionRelative={
                +(
                  project.stats?.expenses
                    .planned_commission_relative_computed ?? 0
                )
              }
              realExpenses={project.stats?.expenses.real_expenses ?? 0}
              realCommission={project.stats?.expenses.real_commission}
              realCommissionRelative={
                +(project.stats?.expenses.real_commission_relative ?? 0)
              }
            />
          );
        },
      },
      {
        id: "budgetExceeded",
        meta: { label: t`Budget exc.` },
        header: t`Budget exc.`,
        orderBy: ProjectOrderByColumnEnum.BudgetExceeded,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            const project = row.original.project;

            return (
              <Column.BudgetExceeded
                fetching={fetching}
                code={project.code}
                budgetExceeded={project.stats?.budget_exceeded ?? 0}
                ourWorkItems={(project.ourWorkOverview || []).map((item) => ({
                  id: item.id,
                  name: item.name,
                  budgetExceeded: item.budget_exceeded,
                }))}
                expensesItems={(project.expensesStructured || []).map(
                  (item) => ({
                    id: item.id,
                    name: item.name,
                    budgetExceeded: item.budget_exceeded,
                  }),
                )}
              />
            );
          }

          return (
            <Column.BudgetExceeded
              code={row.original.project.code}
              budgetExceeded={row.original.subRow.stats.budget_exceeded}
            />
          );
        },
      },
      {
        id: "creditBalance",
        meta: { label: t`Credit balance` },
        header: t`Credit balance`,
        orderBy: ProjectOrderByColumnEnum.CreditBalance,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            const project = row.original.project;

            return (
              <Column.CreditBalance
                fetching={fetching}
                isProBono={!project.billable}
                creditBalance={project.stats?.credit_balance ?? 0}
              />
            );
          }

          return (
            <Column.CreditBalance
              isProBono={!row.original.project.billable}
              creditBalance={row.original.subRow.stats.credit_balance}
            />
          );
        },
      },
      {
        id: "invoicedPercentage",
        meta: { label: t`Invoiced` },
        header: t`Invoiced`,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            const project = row.original.project;

            return (
              <Column.Invoiced
                searchParam={project.code}
                percent={+(project.stats?.invoiced_percent ?? 0)}
              />
            );
          }

          return (
            <Column.Invoiced
              searchParam={row.original.subRow.title}
              percent={+row.original.subRow.stats.invoiced_percent}
            />
          );
        },
      },
      {
        id: "project-categories",
        meta: { label: t`Category` },
        header: t`Category`,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          return (
            <Skeleton
              minH="4"
              isLoaded={Array.isArray(row.original.project.categories)}
            >
              <HStack wrap="wrap" mt="1" whiteSpace="normal">
                {row.original.project.categories?.map(({ id, name }) => (
                  <Badge
                    key={`${id}-${name}`}
                    fontSize="2xs"
                    fontWeight="thin"
                    bg="grey.50"
                    size="xs"
                  >
                    {name}
                  </Badge>
                ))}
              </HStack>
            </Skeleton>
          );
        },
      },
      {
        id: "visible",
        size: 40,
        enableResizing: false,
        meta: { label: t`Client access`, isReorderDisabled: true },
        header: "",
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const project = row.original.project;

          return (
            <ExternalAccessIcon
              key={project.scopes?.length?.toString()}
              type="project"
              id={project.id}
              scopes={project.scopes ?? []}
              isTemplate={project.isTemplate}
            />
          );
        },
      },
      {
        id: "star",
        size: 100,
        enableResizing: false,
        meta: { label: t`Starred` },
        header: t`Starred`,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const project = row.original.project;

          return (
            <StarButton
              isStarred={project.is_starred}
              isLoading={store.starProjectModalStore.isLoading(project.id)}
              onClick={() => {
                store.starProjectModalStore.modalState.onOpen({
                  ids: project.id,
                  action: project.is_starred ? "unstar" : "star",
                });
              }}
              label={(starred) =>
                starred ? t`Unstar project` : t`Star project`
              }
            />
          );
        },
      },
      {
        id: "project-stage",
        meta: { label: t`Stage` },
        header: t`Stage`,
        cell: ({ row }) =>
          isProjectRow(row.original) ? row.original.project.stage?.name : null,
      },
      {
        id: "note",
        size: 61,
        meta: { label: t`Note` },
        header: t`Note`,
        cell: ({ row }) =>
          isProjectRow(row.original) ? (
            <Column.Note
              note={row.original.project.note ?? undefined}
              id={row.original.project.id}
            />
          ) : null,
      },
      {
        id: ACTIONS_COLUMN_ID,
        size: 60,
        enableResizing: false,
        meta: { label: t`Actions`, isReorderDisabled: true },
        header: t`Actions`,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const project = row.original.project;

          if (!project.id || !project.projectManager || fetching) {
            return null;
          }

          return (
            <Column.Actions
              project={{
                ...project,
                billable: project.billable ?? false,
              }}
            />
          );
        },
      },
    ],
    [fetching],
  );

  return (
    <>
      <DataTable.Body
        tableKey="projects"
        data={data}
        columns={columns}
        orderedBy={store.orderBy.clauses[0]}
        onOrderByChange={({ column, order }) => {
          if (!order) {
            store.orderBy.setClauses([]);
            store.pagination.resetPage();
            return;
          }

          store.handleOrderChange({
            column,
            order,
          });
        }}
        selected={store.selectedProjectsIds}
        onSelect={store.setSelectedProjectIds}
        allIds={store.projectsSummary?.projectIds}
        bulkActions={store.bulkActionsStore.bulkActions}
        enableSubRowSelection={false}
        onExpandRow={(row) => {
          if (!isProjectRow(row)) {
            return;
          }

          store.handleUncollapseRow(row.project.id);
        }}
        onExpandAllRows={store.handleUncollapseAll}
      />
      <Pagination store={store.pagination} />
      <ModalConfirm
        isOpen={store.bulkActionsStore.confirmDialog.isOpen}
        onClose={store.bulkActionsStore.confirmDialog.onClose}
        onConfirm={store.bulkActionsStore.confirm}
        isLoading={store.bulkActionsStore.isConfirmDialogLoading}
      />
      <ModalConfirm
        isOpen={store.starProjectModalStore.modalState.isOpen}
        onClose={store.starProjectModalStore.modalState.onClose}
        onConfirm={store.starProjectModalStore.confirm}
        isLoading={store.starProjectModalStore.isAnyLoading}
        heading={
          store.starProjectModalStore.isActionStar
            ? t`Star project(s)`
            : store.starProjectModalStore.isActionUnstar
              ? t`Unstar project(s)`
              : undefined
        }
        message={
          <VStack alignItems="start" spacing={2}>
            <Checkbox
              onChange={({ target }) =>
                store.starProjectModalStore.handleWithMyTasks(target.checked)
              }
            >
              {store.starProjectModalStore.isActionStar ? (
                <Trans>
                  Also star all my tasks related to selected projects
                </Trans>
              ) : store.starProjectModalStore.isActionUnstar ? (
                <Trans>
                  Also unstar all my tasks related to selected projects
                </Trans>
              ) : null}
            </Checkbox>
            <p>{<Trans>Are you sure?</Trans>}</p>
          </VStack>
        }
      />
    </>
  );
};

export default observer(TableBody);
